import { useState, Suspense, Fragment } from "react"
import { Container, Row, Col, Spinner, Breadcrumb } from "react-bootstrap"
import Link from "next/link"
import ReactPlayer from "react-player"
import { useRouter } from "next/router"
import styled from "styled-components"
import CreateComment from "../../Comment/components/CreateComment"
import ErrorFallback from "@modules/Common/components/ErrorFallback"
import Comments from "@modules/Comment/components/Comments"
import RelativeActivities from "./RelativeActivities"
import Image from "next/image"
import ErrorBoundary from "@modules/Common/components/ErrorBoundary"
import { CommentActivityType, StudentActivityWithoutQuestionsQuery, useStudentActivityWithoutQuestionsQuery, useUnlockStudentActivityMutation } from "@generated/graphql"
import StudentSelfComments from "@modules/Comment/components/StudentSelfComments"
import { isEmptyString } from "@utils/stringUtils"

const StyledBadgeSpan = styled.span`
  font-weight: 700;
  background: url('/assets/svg/badge-background.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  margin: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.3em;
`

const StyledSkillNameSpan = styled.div`
  text-align: center;
  display: inline-block;
  font-weight: 700;
  background:url(/assets/svg/ActivityDetailSkillName.svg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  color: white;
  border-radius: 8px;
  margin: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.3em;
`

const StyledOverlay = styled.div`
  border-radius: 16px;
  margin-top: 24px;
  margin-left: 16px;
  background: #EAEDEC;
  padding: 48px 24px 36px 48px;
  text-align: left;
  margin-bottom: 24px;
  @media (max-width: 601px) {
    padding: 24px 12px 18px 24px;
  }
`

const StyledBox = styled.div`
  position: absolute;
  text-align: left;
  font-weight: bolder;
  font-size: 1.5em;
  @media (max-width: 601px) {
    font-size: 1.2em;
    & > img {
      width: 90%;
    }
  }
`

const StyledBoxHeader = styled.div`
  position: absolute;
  top: 5px;
  left: 42px;
  color: white;
  text-transform: uppercase;
`

const StyledButton = styled.button`
  background: url(/assets/img/button.png);
  background-repeat: no-repeat;
  background-position: center;
  padding: 0.25em 1.5em;
  background-size: 100% 100%;
  border: none;
  font-weight: 700;
  font-size: 1.2em;
  margin: 0.75em 0;
`

const StyledActivityHeader = styled.div`
  @media (max-width: 601px) {
    margin: 16px 24px;
  }
`

interface IActivity {
  slug: string
}

export default function ActivityOpenDetailView(props: IActivity) {
  const [showRelativeActivities, setShowRelativeActivities] = useState(false)
  const [activity, setActivity] = useState<StudentActivityWithoutQuestionsQuery["studentActivity"]>()
  const router = useRouter()

  const { loading: studentActivityWithoutQuestionsLoading, data: studentActivityWithoutQuestionsData, error: studentActivityWithoutQuestionsError } = useStudentActivityWithoutQuestionsQuery({
    variables: {
      where: { slug: props.slug }
    },
    onCompleted(data) {
      setActivity(data.studentActivity)
    },
  })

  return (
    <Container fluid className="h-100">
      {activity && <>
        <Row>
          <Col xs={{ offset: 1 }} md={{ offset: 2 }}>
            <Breadcrumb>
              <Breadcrumb.Item linkAs={Link} href="/dashboard">Home</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={Link} href="/skills">Skills</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={Link} href={`/skills/${activity.skill?.slug}`}>{activity.skill?.name}</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={Link} href={`/skills/${activity.slug}`} active>{activity.title}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <Row>
          <Col lg={{ span: 3, offset: 2 }} md={{ span: 6, offset: 2 }} xs={{ span: 8, offset: 1 }} style={{ textAlign: "right" }}>
            <img src={activity?.image || activity?.featuredImage || "/assets/img/logo.png"} style={{ maxWidth: "240px" }} />
          </Col>
          <Col lg={{ span: 4 }} style={{ textAlign: "left", position: "relative" }}>
            <StyledActivityHeader>
              <h1>{activity?.title}</h1>
              {!isEmptyString(activity?.shortDescription) &&
                <div dangerouslySetInnerHTML={{ __html: activity?.shortDescription || "" }} />}
              <div style={{ marginTop: "20px" }}>
                <StyledBadgeSpan>+{activity?.xps}XPs</StyledBadgeSpan>
                <StyledBadgeSpan>{activity?.difficultyLevel}</StyledBadgeSpan>
                <StyledBadgeSpan><img src="/assets/svg/ActivityDetailMinTag.svg" width="16px" />{activity?.timeRequired}</StyledBadgeSpan>
              </div>
              <br />
              <div>Tags: {activity.tags?.map((tag, index) => (<Fragment key={tag.id}>{(index ? " , " : "")}<Link href={`/tags/${tag.slug}`}>{tag.name}</Link></Fragment>))}</div>
            </StyledActivityHeader>
          </Col>
        </Row>
      </>}
      <Row>
        <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-3 rounded-lg">
          <StyledBox>
            <img src="/assets/svg/ActivityDetailChallenge.svg" />
            <StyledBoxHeader>Challenge</StyledBoxHeader>
          </StyledBox>
          <StyledOverlay>
            {activity?.challengeStatement && activity?.challengeStatement !== "<p><br></p>" &&
              <div dangerouslySetInnerHTML={{ __html: activity?.challengeStatement }} />}
            {activity?.materialsNeeded && activity?.materialsNeeded !== "<p><br></p>" &&
              <div dangerouslySetInnerHTML={{ __html: activity?.materialsNeeded }} />}
            <h3 style={{ color: "#6630da", fontWeight: "bolder", marginTop: "20px" }}>YOU CAN EARN...!</h3>
            <StyledSkillNameSpan>
              {activity?.skill && activity?.skill.name && activity?.skill.name.toUpperCase()}
              <br />
              +{activity?.xps}XPs
            </StyledSkillNameSpan>
          </StyledOverlay>
        </Col>
        {activity?.instructionText || activity?.video || activity?.instructionImage ? <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-5 rounded-lg">
          <StyledBox>
            <StyledBoxHeader>How To Do It</StyledBoxHeader>
            <img src="/assets/svg/ActivityDetailHowToDoItTag.svg" />
          </StyledBox>
          <StyledOverlay>
            {activity?.video && <ReactPlayer
              url={activity?.video}
              width="100%"
              config={{ vimeo: { playerOptions: { controls: true } } }}
            />}
            {activity?.instructionImage && <img src={activity?.instructionImage} style={{ maxWidth: "90%" }} />}
            {activity?.instructionText && activity?.instructionText !== "<p><br></p>" && <div dangerouslySetInnerHTML={{ __html: activity?.instructionText }} />}
          </StyledOverlay>
        </Col> : null}
        {activity?.description && activity?.description !== "<p><br></p>" && <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-5 rounded-lg">
          <StyledBox>
            <StyledBoxHeader>Know More About It?</StyledBoxHeader>
            <img src="/assets/svg/ActivityDetailKnowMoreAboutIt.svg" />
          </StyledBox>
          <StyledOverlay>
            {activity?.description && activity?.description !== "<p><br></p>" &&
              <div dangerouslySetInnerHTML={{ __html: activity?.description }} />}
          </StyledOverlay>
        </Col>}
        {activity?.isCommentEnabled && <>
          <Row>
            <Col lg={{ span: 8, offset: 2 }} xs={{ offset: 0 }} className="bg-white mt-5 rounded-lg">
              <StyledBox>
                <StyledBoxHeader>What Have Others Done?</StyledBoxHeader>
                <img src="/assets/svg/ActivityDetailWhatOthersHaveDoneTag.svg" />
              </StyledBox>
              <StyledOverlay>
                {activity?.id && <ErrorBoundary fallback={ErrorFallback}>
                  <Suspense fallback={<Spinner animation="border" variant="primary" />}>
                    <Comments type={CommentActivityType.ACTIVITY} activitySlug={activity?.slug} />
                    <StudentSelfComments type={CommentActivityType.ACTIVITY} activitySlug={activity?.slug} />
                  </Suspense>
                </ErrorBoundary>}
              </StyledOverlay>
            </Col>
          </Row>
          {showRelativeActivities && activity.skill && <Row>
            <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-5 rounded-lg">
              <StyledBox>
                <StyledBoxHeader>Recommended for you</StyledBoxHeader>
                <img src="/assets/svg/ActivityDetailKnowMoreAboutIt.svg" />
              </StyledBox>
              <StyledOverlay>
                <p>Congratulations for submitting your activity. Here are some interesting activities that you might like to attempt next.</p>
                <RelativeActivities skillSlug={activity.skill.slug} />
              </StyledOverlay>
            </Col>
          </Row>}
          {showRelativeActivities === false && <Row>
            <Col lg={{ span: 8, offset: 2 }} xs={{ span: 12, offset: 0 }} className="bg-white mt-5 rounded-lg">
              <StyledBox>
                <StyledBoxHeader>Submit Your Project</StyledBoxHeader>
                <img src="/assets/svg/ActivityDetailWhatOthersHaveDoneTag.svg" />
              </StyledBox>
              <StyledOverlay>
                <CreateComment activityId={activity?.id} slug={props.slug} setShowRelativeActivities={setShowRelativeActivities} />
              </StyledOverlay>
            </Col>
          </Row>}
        </>}
      </Row>
      {(studentActivityWithoutQuestionsError?.graphQLErrors[0]?.extensions?.code === "BAD_USER_INPUT" || studentActivityWithoutQuestionsError?.graphQLErrors[0]?.extensions?.code === "BAD_USER_INPUT") && <Row>
        <Col lg={{ span: 8, offset: 2 }}>
          <Image src="/assets/img/not-found-placeholder.jpeg" height={400} width={400} alt="Activity Not Found" />
          <div style={{ fontSize: 20, fontWeight: 700 }}>The activity you&apos;re trying to access is not available for your school or standard. Please Contact us if you think you&apos;re looking on the right page.
          </div>
          <StyledButton onClick={() => router.push("/activities")}>
            Check Other Activities
          </StyledButton>
        </Col>
      </Row>}
    </Container>
  )
}
